import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import CssTransition from '../../components/Transition/CssTransition';
import { connect } from 'react-redux';

import SubscribeForm from './SubscribeForm';
import { onSubscribeComplete } from '../../containers/Widget/actions';
import { width, isPortrait } from '../../utils/size';
import { cacheFormData } from '../../utils/formCaching/actions';

const OverlayBackground = styled.div`
    position: absolute;
    display: ${props => (props.isVisible ? 'flex' : 'none')};
    overflow: hidden;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props =>
        props.isVisible ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'};
    z-index: 3;

    &.transition-enter {
        display: flex;
        background-color: rgba(0, 0, 0, 0);
    }

    &.transition-enter.transition-enter-active {
        display: flex;
        background-color: rgba(0, 0, 0, 0.5);
        transition: background-color 0.3s linear;
    }

    &.transition-exit {
        display: flex;
        background-color: rgba(0, 0, 0, 0.5);
    }

    &.transition-exit.transition-exit-active {
        display: flex;
        background-color: rgba(0, 0, 0, 0);
        transition: background-color 0.3s linear;
    }
`;

const AboutUs = styled.div`
    transform: ${props =>
        props.isVisible
            ? `${
                  props.transitionType === 'left-to-right'
                      ? 'translate(0, 0)'
                      : 'translate(262px, 0)'
              }`
            : `${
                  props.transitionType === 'left-to-right'
                      ? 'translate(0, 0)'
                      : 'translate(620px, 0)'
              }`};

    position: relative;
    background-color: rgba(256, 256, 256, 1);
    opacity: 2;
    padding: 1.125em 1em 1.125em 1em;
    flex-direction: column;
    box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.5);
    border: solid 1px #f3f1f1;
    display: inline;

    &.transition-enter {
        transform: ${props =>
            props.transitionType === 'left-to-right'
                ? 'translate(-358px, 0)'
                : 'translate(620px, 0)'};
    }

    &.transition-enter.transition-enter-active {
        transition: transform 0.3s ease-out;
        transform: translate(0, 0);

        transform: ${props =>
            props.transitionType === 'left-to-right'
                ? 'translate(0, 0)'
                : `translate(${props.desktopResolution}px, 0)`};

        @media (max-width: 30em) {
            transform: ${props =>
                props.transitionType === 'left-to-right'
                    ? 'translate(0, 0)'
                    : `translate(${props.mobileResolution}px, 0)`};
        }
    }

    &.transition-exit {
        transform: ${props =>
            props.transitionType === 'left-to-right'
                ? 'translate(0, 0)'
                : `translate(${props.desktopResolution}px, 0)`};

        @media (max-width: 30em) {
            transform: ${props =>
                props.transitionType === 'left-to-right'
                    ? 'translate(0, 0)'
                    : `translate(${props.mobileResolution}px, 0)`};
        }
    }

    &.transition-exit.transition-exit-active {
        transition: transform 0.3s ease-in;
        transform: ${props =>
            props.transitionType === 'left-to-right'
                ? 'translate(-358px, 0)'
                : 'translate(620px, 0)'};
    }

    @media (min-width: 30em) {
        width: 358px;
        transform: ${props =>
            props.transitionType === 'left-to-right'
                ? 'translate(0, 0)'
                : `translate(${props.desktopResolution}px, 0)`};
    }

    @media (max-width: 30em) {
        max-width: 310px;
        width: 100%;
        transform: ${props =>
            props.transitionType === 'left-to-right'
                ? 'translate(0, 0)'
                : `translate(${props.mobileResolution}px, 0)`};
    }
`;

const AboutUsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const AboutUsBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    word-wrap: break-word;

    > div {
        p {
            &:last-child {
                display: ${props => (props.isEmailSubmitted ? 'none' : '')};
            }
        }
    }
`;

const CloseButton = styled.div`
    z-index: 1;
    width: 14px;
    height: 26px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 1.08;
    text-align: left;
    color: #5d656c;
    margin-top: -0.625em;
    cursor: pointer;
`;

const AboutUsTitle = styled.div`
    font-size: 16px;
    line-height: 1.63;
    text-align: left;
    margin-bottom: 0.5em;
`;

const AboutUsText = styled.div`
    font-family: Lato;
    font-size: 14px;
    line-height: 1.43;
    text-align: left;
    color: ${props => (props.isMobileResolution ? '#5d656c' : '#333333')};
    ${props => (props.allowScroll && !isPortrait() ? 'overflow-y: auto; height: 240px;' : '')};
    ${props => (props.allowScroll && isPortrait() ? 'overflow-y: auto; height: 380px;' : '')};

    @media (min-width: 30em) {
        ${props => (props.isRockTheVote ? 'overflow-y: scroll; height: 240px;' : '')};
    }

    &::-webkit-scrollbar {
        width: 4px;
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background: #ff245e;
    }
`;

const Body = styled.div`
    ${props =>
        !props.isPartnerOrPublisherOverlay &&
        `
            margin: 0 15px 0 0;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
        `};
`;

const mobileResolution = () => {
    if (width() < 300) return 0;
    return width() - 300;
};

const desktopResolution = () => {
    var windowInnerWidth = 0;
    if (width() < 480) return 0;
    if (width() > 630) {
        windowInnerWidth = 630;
    }
    if (width() === 630) return width() - 368;
    if (windowInnerWidth > 0) {
        return windowInnerWidth - 358;
    } else {
        return width() - 358;
    }
};

const Overlay = props => {
    const Logo = props.logoComponent;
    const AboutUsTextContent = props.aboutUsComponent;

    return (
        <CssTransition in={props.isVisible}>
            <OverlayBackground
                isVisible={props.isVisible}
                onClick={() =>
                    props.setOverlayVisible(
                        false,
                        undefined,
                        false,
                        props.isActionChoiceOverlay
                    )
                }
            >
                <CssTransition in={props.isVisible}>
                    <AboutUs
                        onExit={() =>
                            props.setOverlayVisible(
                                false,
                                undefined,
                                false,
                                props.isActionChoiceOverlay
                            )
                        }
                        isVisible={props.isVisible}
                        onClick={proxy => proxy.stopPropagation()}
                        mobileResolution={mobileResolution()}
                        desktopResolution={desktopResolution()}
                        transitionType={props.transitionType}
                    >
                        <AboutUsHeader>
                            <Logo
                                onClick={() =>
                                    props.setOverlayVisible(
                                        false,
                                        undefined,
                                        false,
                                        props.isActionChoiceOverlay
                                    )
                                }
                                logoUrl={
                                    props.isRockTheVote
                                        ? props.logo
                                        : props.aboutUsContent.logoUrl
                                }
                                isSmall={props.logoIsSmall || false}
                                marginBottom={'0'}
                            />
                            <CloseButton
                                onClick={() =>
                                    props.setOverlayVisible(
                                        false,
                                        undefined,
                                        false,
                                        props.isActionChoiceOverlay
                                    )
                                }
                            >
                                +
                            </CloseButton>
                        </AboutUsHeader>
                        <Body
                            isPartnerOrPublisherOverlay={
                                props.isPartnerOrPublisherOverlay
                            }
                        >
                            <AboutUsBody
                                noTitle={props.title === ''}
                                isEmailSubmitted={props.isEmailSubmitted}
                            >
                                <AboutUsTitle>
                                    {props.isRockTheVote ? props.name : props.title}
                                </AboutUsTitle>
                                <AboutUsText
                                    isMobileResolution={props.isMobileResolution}
                                    isRockTheVote={props.isRockTheVote}
                                    allowScroll={props.allowScroll}
                                >
                                    <AboutUsTextContent
                                        description={
                                            props.isRockTheVote
                                                ? props.description
                                                : props.aboutUsContent.description
                                        }
                                    />
                                </AboutUsText>
                            </AboutUsBody>
                            {props.subscribeForm && (
                                <SubscribeForm
                                    onSubmit={values => {
                                        props.cacheFormData(values);
                                        props.onSubscribeComplete(
                                            values,
                                            props.activeAction.type,
                                            null,
                                            null
                                        );
                                    }}
                                    isEmailSubmitting={props.isEmailSubmitting}
                                    isEmailSubmitted={props.isEmailSubmitted}
                                />
                            )}
                        </Body>
                    </AboutUs>
                </CssTransition>
            </OverlayBackground>
        </CssTransition>
    );
};
Overlay.propTypes = {
    allowScroll: PropTypes.bool,
    isVisible: PropTypes.bool,
    isActionChoiceOverlay: PropTypes.bool,
    isMobileResolution: PropTypes.bool.isRequired,
    setOverlayVisible: PropTypes.func.isRequired,
    logoComponent: PropTypes.func.isRequired,
    onSubscribeComplete: PropTypes.func.isRequired,
    aboutUsComponent: PropTypes.func.isRequired,
    aboutUsContent: PropTypes.object,
    transitionType: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subscribeForm: PropTypes.bool,
    isEmailSubmitting: PropTypes.bool,
    logoIsSmall: PropTypes.bool,
    isEmailSubmitted: PropTypes.bool,
    isPartnerOrPublisherOverlay: PropTypes.bool,
    logo: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    isRockTheVote: PropTypes.bool,
    activeAction: PropTypes.object,
    cacheFormData: PropTypes.func.isRequired
};

export default connect(
    state => ({
        isEmailSubmitting: state.widget.isEmailSubmitting,
        isEmailSubmitted: state.widget.isEmailSubmitted,
        isRockTheVote: state.widget.isRockTheVote,
        activeAction: state.widget.activeAction
    }),
    { onSubscribeComplete, cacheFormData }
)(Overlay);
